import { useCallback, useEffect, useRef } from 'react'

import { useReducerWithLogger } from 'shared/hooks'

import { STATUS_SUCCESS } from '../Noty.constants'

/**
 * @typedef {import('../Noty.constants').TNotyItem} TNotyItem
 * @typedef {import('../Noty.constants').TNotyConfirm} TNotyConfirm
 */

export const ADD = 'ADD'
export const REMOVE = 'REMOVE'
export const REMOVE_ALL = 'REMOVE_ALL'
export const SHOW_CONFIRM = 'SHOW_CONFIRM'
export const HIDE_CONFIRM = 'HIDE_CONFIRM'

/**
 * @type {TNotyState} initialState
 */
export const initialState = {
  notys: [],
  confirm: {
    danger: false,
    isVisible: false,
    title: '',
    onConfirm: null,
  },
}

/**
 * This function will remove duplicates and also error noty (when the next item has a SUCCESS status).
 * It will then push the nextItem at the beginning of the array
 * @param {Array.<TNotyItem>} items
 * @param {TNotyItem} nextItem
 * @returns {Array.<TNotyItem>}
 */
export const filterThenAddNoty = (items, nextItem) => {
  // remove duplicates
  let filteredItems = items.filter(item => {
    if (item.content === nextItem.content && item.status === nextItem.status) {
      return false
    }
    return true
  })

  // if group exists and it's a success noty, then remove all other notys within the same group
  if (nextItem.status === STATUS_SUCCESS && !!nextItem.group) {
    filteredItems = filteredItems.filter(item => {
      // group need to be active on the item, otherwise we'll always keep it in the list
      const isSameGroup = !!item.group && item.group === nextItem.group
      return !isSameGroup
    })
  }

  return [nextItem, ...filteredItems]
}

/**
 *
 * @param {TNotyState} state
 * @param {object} action
 * @returns {TNotyState}
 */
export const notyReducer = (state, action) => {
  let newNoty = null

  switch (action.type) {
    case ADD:
      newNoty = {
        id: action.id || +new Date(),
        content: action.content,
        status: action.status,
        opts: action.opts || {},
        group: action.opts?.group,
      }
      return {
        ...state,
        notys: filterThenAddNoty(state.notys, newNoty),
      }
    case REMOVE:
      return { ...state, notys: state.notys.filter(t => t.id !== action.id) }
    case REMOVE_ALL:
      return { ...state, notys: [] }
    case SHOW_CONFIRM:
      return {
        ...state,
        notys: [], // clearing up notys when opening a confirm modal
        confirm: {
          ...state.confirm,
          isVisible: true,
          onConfirm: action.onConfirm,
          onClose: action.onClose,
          title: action.title,
          danger: !!action.danger,
          primaryButtonText: action.primaryButtonText,
          secondaryButtonText: action.secondaryButtonText,
          body: action.body,
          modalWidth: action.modalWidth,
          modalHeight: action.modalHeight,
          fixedPosition: action.fixedPosition,
          closeOnConfirm: action.closeOnConfirm,
        },
      }
    case HIDE_CONFIRM:
      return { ...state, confirm: { ...state.confirm, isVisible: false } }
    default:
      return state
  }
}

/**
 * Leverage `useReducerWithLogger` to initialize a hook reducer for the noty component
 * @returns {[notys: Array.<TNotyItem>, confirm: TNotyConfirm, dispatch]}
 */
export const useNotyReducer = () => {
  const [data, dispatch] = useReducerWithLogger(
    notyReducer,
    initialState,
    process.env.NODE_ENV === 'development',
    'noty'
  )

  // Guard against dispatching after unmount (React 17 warning)
  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    []
  )

  const safeDispatch = useCallback(
    action => {
      if (mountedRef.current) {
        dispatch(action)
      }
    },
    [dispatch]
  )

  /** @type {{notys: Array.<TNotyItem>, confirm: TNotyConfirm}} */
  const { notys, confirm } = data

  return [notys, confirm, safeDispatch]
}
